import React from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * One line of context above the step's controls.
 *
 * The step's title lives in the wizard header, so repeating it here would push
 * the actual control further down the page — the exact reason people were
 * scrolling past the home address and radius inputs.
 */
export const StepIntro: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-4 text-sm leading-relaxed text-ink-500">{children}</p>
);

export const Section: React.FC<{
  title: string;
  description?: string;
  required?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, required, action, children }) => (
  <section className="rounded-xl border border-ink-200 bg-surface p-4 sm:p-5">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-ink-900">
          {title}
          {required && (
            <span className="ml-1 text-danger-500" aria-hidden="true">
              *
            </span>
          )}
        </h3>
        {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

const calloutTones = {
  info: 'border-ink-200 bg-ink-50 text-ink-600',
  warning: 'border-warning-200 bg-warning-50 text-warning-800',
  success: 'border-success-200 bg-success-50 text-success-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
} as const;

const calloutIconTones = {
  info: 'text-ink-400',
  warning: 'text-warning-600',
  success: 'text-success-600',
  danger: 'text-danger-600',
} as const;

export const Callout: React.FC<{
  tone?: keyof typeof calloutTones;
  icon?: LucideIcon;
  children: React.ReactNode;
}> = ({ tone = 'info', icon: Icon, children }) => (
  <div className={`flex items-start gap-2.5 rounded-lg border p-3 ${calloutTones[tone]}`}>
    {Icon && <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${calloutIconTones[tone]}`} />}
    <div className="min-w-0 text-xs leading-relaxed">{children}</div>
  </div>
);

export const FieldLabel: React.FC<{
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}> = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink-700">
    {children}
    {required && (
      <span className="ml-0.5 text-danger-500" aria-hidden="true">
        *
      </span>
    )}
  </label>
);

/**
 * Secondary detail that would otherwise compete with the step's main control.
 * Collapsed by default so the fold stays clean on short viewports.
 */
export const Disclosure: React.FC<{
  summary: string;
  children: React.ReactNode;
}> = ({ summary, children }) => (
  <details className="group rounded-xl border border-ink-200 bg-surface">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50">
      {summary}
      <span className="text-xs text-ink-400 transition-transform group-open:rotate-180" aria-hidden="true">
        ▾
      </span>
    </summary>
    <div className="border-t border-ink-100 px-4 py-3">{children}</div>
  </details>
);
